"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import AutoScroll from "embla-carousel-auto-scroll";
import { cn } from "@/lib/utils";
import { PortableText } from "next-sanity";
import { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import { resolveLink } from "@/sanity/globals/CTA";

export interface CertificateSectionV2Data {
  chip?: string;
  avatarImages?: GROQImage[];
  title?: PortableTextBlock[];
  text?: PortableTextBlock[];
  highlightedText?: string;
  images?: GROQImage[];
  ctaUrl?: string;
}

function parseChipText(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="text-[#d6eee7] font-bold font-geist-mono">
        {part}
      </span>
    ) : (
      part
    ),
  );
}
const ease = [0.25, 0.1, 0.25, 1] as const;

function DotArrow({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill="none"
    >
      <rect
        x="4.88269"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="4.88269"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="9.75452"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="9.75452"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="9.75452"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="9.75452"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="14.6371"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="14.6371"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="14.6371"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="14.6371"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="14.6371"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="19.509"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="19.509"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="19.509"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
      <rect
        x="24.3906"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="currentColor"
      />
    </svg>
  );
}

const AVATAR_COLORS = ["#2a6b4f", "#1e5a42", "#357a5c", "#245040"];

export default function CertificateSectionV2({
  data,
  language,
  compactMobile = false,
}: {
  data: CertificateSectionV2Data;
  language: string;
  compactMobile?: boolean;
}) {

  const link = resolveLink(data?.ctaUrl || "/", language);
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const plugins = useMemo(() => compactMobile ? [AutoScroll({
    speed: 0.75,
    startDelay: 0,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
    breakpoints: {
      "(min-width: 993px)": { active: false },
      "(prefers-reduced-motion: reduce)": { active: false },
    },
  })] : [], [compactMobile]);

  if (!data.title && !data.images?.length) return null;

  return (
    <section data-home-certificates={compactMobile || undefined} className={cn("flex relative flex-col items-center bg-[#001B14] z-10 pb-[5rem] pt-[8rem] lg:py-[15rem] w-full overflow-x-clip", compactMobile && "max-lg:pb-0")}>
      {/* Chip + Title */}
      <div className={cn("flex flex-col items-center text-center w-[min(90rem,100%)] px-[2rem] lg:px-0 gap-[3rem]", compactMobile && "max-lg:gap-[2rem]")}>
        {data.chip && (
          <div className="flex items-center gap-[1.5rem]">
            <div className="flex items-center">
              {data.avatarImages && data.avatarImages.length > 0
                ? data.avatarImages.slice(0, 3).map((img, i) => (
                    <div
                      key={i}
                      className="w-[3.75rem] h-[3.75rem] rounded-full border border-[rgba(113,169,97,0.3)] flex-shrink-0 overflow-hidden bg-[#127259]"
                      style={{ marginLeft: i > 0 ? "-1.25rem" : "0" }}
                    >
                      <GroqImage
                        src={img}
                        alt={img.name || ""}
                        className="!w-full !h-full object-cover"
                      />
                    </div>
                  ))
                : AVATAR_COLORS.slice(0, 3).map((color, i) => (
                    <div
                      key={i}
                      className="w-[3.75rem] h-[3.75rem] rounded-full border border-[rgba(113,169,97,0.3)] flex-shrink-0 overflow-hidden"
                      style={{
                        marginLeft: i > 0 ? "-1.25rem" : "0",
                        background: color,
                      }}
                    >
                      <svg
                        viewBox="0 0 40 40"
                        fill="none"
                        className="w-full h-full"
                      >
                        <circle
                          cx="20"
                          cy="16"
                          r="7"
                          fill="rgba(255,255,255,0.35)"
                        />
                        <ellipse
                          cx="20"
                          cy="34"
                          rx="12"
                          ry="8"
                          fill="rgba(255,255,255,0.25)"
                        />
                      </svg>
                    </div>
                  ))}
            </div>
            <span className={cn("font-geist-mono text-[2rem] leading-[1.5] text-white tracking-[-0.1rem]", compactMobile && "max-lg:font-geist")}>
              {parseChipText(data.chip)}
            </span>
          </div>
        )}

        {data.title && (
          <div className={cn("richtext prose-headings:mb-0 prose-headings:text-white prose-p:text-white prose-headings:font-medium prose-headings:leading-[1.15] flex flex-col gap-[2rem]", compactMobile && "max-lg:gap-[2.5rem] max-lg:prose-headings:text-[4.25rem] max-lg:prose-headings:leading-[1.15] max-lg:prose-p:text-[2.25rem] max-lg:prose-p:leading-[1.33] max-lg:prose-p:w-[35rem] max-lg:prose-p:max-w-full max-lg:prose-p:mx-auto")}>
            <PortableText value={data.title} components={richTextComponent} />
          </div>
        )}
      </div>

      {/* Carousel */}
      {data.images && data.images.length > 0 && (
        <div className={cn("w-full mt-[6rem] mb-[6rem] relative", compactMobile && "max-lg:mt-[3rem] max-lg:mb-[3.625rem]")}>
          <Carousel
            opts={{ align: "start", loop: true, dragFree: true }}
            plugins={plugins}
            className="w-full"
          >
            <CarouselContent className={cn("-ml-[3rem]", compactMobile && "max-lg:-ml-[2.25rem]")}>
              {data.images.map((img, i) => (
                <CarouselItem
                  key={i}
                  className={cn("pl-[3rem] basis-[85%] lg:basis-[26%]", compactMobile && "max-lg:basis-[min(40rem,100%)] max-lg:pl-[2.25rem]")}
                >
                  <div className="aspect-[369/251]  overflow-hidden rounded-[0.25rem]">
                    <GroqImage
                      src={img}
                      alt={img.name || ""}
                      className="!w-full !h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Left gradient fade — matches Figma */}
          <div className={cn("absolute left-0 top-0 bottom-0 w-[25%] bg-gradient-to-r from-[#001b14] to-transparent pointer-events-none z-10", compactMobile && "max-lg:w-[14.5rem]")} />
          {/* Right gradient fade */}
          <div className={cn("absolute right-0 top-0 bottom-0 w-[15%] bg-gradient-to-l from-[#001b14] to-transparent pointer-events-none z-10", compactMobile && "max-lg:w-[14.5rem]")} />
        </div>
      )}

      {/* Text below carousel */}
      {data.text && (
        <div className={cn("richtext prose-p:text-white/60 prose-p:text-[2rem] prose-p:leading-[1.6] prose-p:mb-0 text-center w-[min(55rem,90%)] mb-[6rem]", compactMobile && "max-lg:w-[min(49.75rem,calc(100%-4rem))] max-lg:mb-[2.75rem] max-lg:prose-p:text-[2.25rem] max-lg:prose-p:leading-[1.5] max-lg:prose-p:text-white/70")}>
          <PortableText value={data.text} components={richTextComponent} />
        </div>
      )}

      {/* Highlighted CTA card */}
      {data.highlightedText &&
        (finalLink ? (
          <Link
            href={finalLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("group bg-white px-[7rem] py-[2.5rem] flex items-center justify-between w-[min(91rem,90%)] transition-transform duration-300 hover:-translate-y-[0.5rem] hover:shadow-[0_2rem_4rem_rgba(0,0,0,0.4)]", compactMobile && "max-lg:w-[min(49.75rem,calc(100%-4rem))] max-lg:px-[1.875rem] max-lg:py-[2.6875rem] max-lg:gap-[0.375rem]")}
          >
            <DotArrow className="w-[3.5rem] h-[3.5rem] text-black flex-shrink-0 transition-transform duration-300 group-hover:-translate-x-[0.5rem]" />
            <p className={cn("mb-0 font-geist text-[2rem] leading-[1.5] text-black font-semibold flex-1 text-center tracking-[-0.1rem] px-[3rem]", compactMobile && "max-lg:px-0")}>
              {data.highlightedText}
            </p>
            <DotArrow className="w-[3.5rem] h-[3.5rem] text-black rotate-180 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-[0.5rem]" />
          </Link>
        ) : (
          <div className={cn("bg-white px-[7rem] py-[2.5rem] flex items-center justify-between w-[min(91rem,90%)]", compactMobile && "max-lg:w-[min(49.75rem,calc(100%-4rem))] max-lg:px-[1.875rem] max-lg:py-[2.6875rem] max-lg:gap-[0.375rem]")}>
            <DotArrow className="w-[3.5rem] h-[3.5rem] text-black flex-shrink-0" />
            <p className={cn("mb-0 font-geist text-[2rem] leading-[1.5] text-black font-semibold flex-1 text-center tracking-[-0.1rem] px-[3rem]", compactMobile && "max-lg:px-0")}>
              {data.highlightedText}
            </p>
            <DotArrow className="w-[3.5rem] h-[3.5rem] text-black rotate-180 flex-shrink-0" />
          </div>
        ))}
    </section>
  );
}
